import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Notification } from "../../interfaces/interfaces";
import { clearAsyncNotification } from "./notificationUtil";

export interface NotificationState {
  all: {
    unreadCount: number;
    reset: boolean;
    notifications: Notification[];
  };
  general: {
    unreadCount: number;
    reset: boolean;
    notifications: Notification[];
  };
  collaboration: {
    unreadCount: number;
    reset: boolean;
    notifications: Notification[];
  };
  analytics: {
    unreadCount: number;
    reset: boolean;
    notifications: Notification[];
  };
}

export const notificationEventTypes = {
  collaboration: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  general: [12],
  analytics: [13, 14, 15, 16],
};

const INITIAL_STATE = {
  all: {
    unreadCount: 0,
    reset: false,
    notifications: [],
  },
  general: {
    unreadCount: 0,
    reset: false,
    notifications: [],
  },
  collaboration: {
    unreadCount: 0,
    reset: false,
    notifications: [],
  },
  analytics: {
    unreadCount: 0,
    reset: false,
    notifications: [],
  },
} as NotificationState;

type AddNotificationsPayload = {
  category: string;
  unreadCount: number;
  notifications: Notification[];
  isInitial: boolean;
};

type AddSingleNotificationPayload = {
  notification: Notification;
};

type ChangeResetPayload = {
  type: string;
  value: boolean;
};

const NotificationSlice = createSlice({
  name: "notification",
  initialState: INITIAL_STATE,
  reducers: {
    addNotifications: (
      state,
      action: PayloadAction<AddNotificationsPayload>
    ) => {
      let oldNotifications = state[action.payload.category]
        .notifications as Notification[];
      state[action.payload.category].unreadCount = action.payload.unreadCount;
      if (action.payload.isInitial) {
        state[action.payload.category].notifications = [
          ...action.payload.notifications,
        ];
      } else {
        state[action.payload.category].notifications = [
          ...oldNotifications,
          ...action.payload.notifications,
        ];
      }
    },
    addSingleNotification: (
      state,
      action: PayloadAction<AddSingleNotificationPayload>
    ) => {
      //
      state.all.unreadCount = state.all.unreadCount + 1;
      state.all.notifications = [
        action.payload.notification,
        ...state.all.notifications,
      ];

      const matchedKey = Object.keys(notificationEventTypes).reduce(
        (prev, curVal) => {
          if (prev == null) {
            if (
              notificationEventTypes[curVal].includes(
                action.payload.notification.notificationEvent
              )
            ) {
              prev = curVal;
            }
          }
          return prev;
        },
        null
      );
      //
      if (matchedKey !== null) {
        state[matchedKey].unreadCount = state[matchedKey].unreadCount + 1;
        state[matchedKey].notifications = [
          action.payload.notification,
          ...state[matchedKey].notifications,
        ];
      }
    },
    changeResetStatus: (state, action: PayloadAction<ChangeResetPayload>) => {
      state[action.payload.type].reset = action.payload.value;
      state.all.reset = action.payload.value;
    },
    updateAllNotifications: (state) => {
      state.all.reset = true;
      state.general.reset = true;
      state.collaboration.reset = true;
      state.analytics.reset = true;
    },
    resetNotification: (state) => INITIAL_STATE,
  },
  extraReducers: (builder) => {
    builder.addCase(clearAsyncNotification.fulfilled, (state, action) => {
      if (action.payload !== null) {
        const {
          matchedKey,
          matchedKeyNotifications,
          matchedKeyUnreadCount,
          allNotificationUnreadCount,
          allNotifications,
        } = action.payload;
        state[matchedKey].notifications = matchedKeyNotifications;
        state[matchedKey].unreadCount = matchedKeyUnreadCount;
        state.all.notifications = allNotifications;
        state.all.unreadCount = allNotificationUnreadCount;
      }
    });
  },
});

export const {
  addNotifications,
  addSingleNotification,
  changeResetStatus,
  updateAllNotifications,
  resetNotification,
} = NotificationSlice.actions;

export const NotificationReducer = NotificationSlice.reducer;
